import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { Presence } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import { CustomAwayStatus } from 'components/presence/components/customAwayStatus';

import PresenceOverlay from '../containers/presenceOverlay';
import type { UserPresence } from '../reducers/presence';

import styles from './presenceListItem.module.scss';

type PresenceListItemProps = {
  userPresence: UserPresence;
  ownPresence: Presence;
  ownProfile: User;
  awayStatuses: AwayStatus[];
};

export const PresenceListItem = ({
  awayStatuses,
  userPresence,
  ownProfile,
  ownPresence,
}: PresenceListItemProps): JSX.Element => {
  const isOwnAgent = ownProfile.id === userPresence.user.id;
  const { user: userPresenceUser, presence: userPresencePresence } =
    userPresence;
  const isInACW = userPresencePresence.presence_status.load_descriptions.some(
    (loadDescription) => loadDescription.load_type === 'acw',
  );

  const hasActivePhoneConversation =
    userPresencePresence.presence_status.load_descriptions.findIndex(
      (loadDescription) =>
        (loadDescription.conversation_channel === 'pstnphone' ||
          (loadDescription.conversation_channel === 'callback' &&
            (loadDescription.conversation === null ||
              loadDescription.conversation?.is_callback_consumed === false))) &&
        loadDescription.load_type !== 'acw' &&
        loadDescription.load_type !== 'nonlive',
    ) > -1;
  const simRingEnabled =
    userPresencePresence.presence_status.simultaneous_ringing === true;

  const isWorking = userPresencePresence.status === 'working';
  const isAway = userPresencePresence.status === 'away';
  const isOffline = userPresencePresence.status === 'offline';

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootOnline]: isWorking,
    [styles.rootAway]: isAway,
    [styles.rootOffline]: isOffline,
  });

  const isWorkingWithoutActiveChannel =
    isWorking &&
    userPresencePresence.working_channels.every(({ working }) => !working);

  let icon = isWorkingWithoutActiveChannel ? 'circle' : 'circle-filled';
  let tooltip = isWorkingWithoutActiveChannel
    ? 'Online without active channel'
    : userPresencePresence.status;

  if (hasActivePhoneConversation) {
    icon = 'phone-filled';
    tooltip += ' - On a call';
  } else if (isInACW) {
    icon = 'circle-z';
    tooltip += ' - Wrap-up time';
  } else if (simRingEnabled) {
    icon = 'circle-simultaneous';
    tooltip += ' - Simultaneous ring enabled';
  }

  const { custom_away_status_id, status_updated_at } =
    userPresencePresence.presence_status;

  const customAwayStatus = useMemo(() => {
    if (!isAway || !awayStatuses.length) {
      return null;
    }

    const awayStatus = awayStatuses.find(
      ({ id }) => id === custom_away_status_id,
    );
    if (!awayStatus) {
      return null;
    }

    return (
      <CustomAwayStatus
        awayStatus={awayStatus}
        statusUpdatedAt={status_updated_at}
      />
    );
  }, [isAway, custom_away_status_id, status_updated_at, awayStatuses]);

  return (
    <li className={rootClass} onClick={() => ampli.presenceListItemClicked()}>
      <PresenceOverlay
        isOwnAgent={isOwnAgent}
        ownAgentId={ownProfile.id}
        ownPresence={ownPresence}
        presence={userPresencePresence}
        user={userPresenceUser}
      >
        <div className={styles.container}>
          <Tooltip className={styles.iconTooltip} content={tooltip}>
            {/* @ts-expect-error Custom size not supported by types, but in practice it works */}
            <Icon name={icon} className={styles.icon} size={12} />
          </Tooltip>
          <span className={styles.name}>
            {userPresenceUser.name} {isOwnAgent ? ' (you)' : ''}
          </span>
          {customAwayStatus}
        </div>
      </PresenceOverlay>
    </li>
  );
};
